import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { withFirebase } from "../../../Firebase";
import { calculateCategoryRisk, calculateOverallRisk } from "../../../ViewUserPage/components/RiskAssessment/riskAssessmentUtils";
import { RISK_FACTORS, HIGH_RISK_SPECIFIC_QUESTIONS } from "../../../ViewUserPage/components/constants";
import { riskQuestionsService } from "../../../../services/riskQuestionsService";
import {
  getLocalStorageData,
  setLocalStorageData,
} from "src/utils/localStorageUtils";
import moment from "moment";
import DropoutAnalysisTab from "../../../ViewClassroom/components/ClassRiskAssessment/DropoutAnalysisTab";
import "./index.css";
import { isDropout } from "src/utils/exitStatus";

const STORAGE_KEY = "adminDropoutAnalysis__selectedClassrooms";

const FILTERED_EMAILS = ["inaguirre96@gmail.com", "ignacio@stoprecidivism.org"];
const FILTERED_DOMAINS = ["@stoprecidivism.org", "@myreded.com"];

const isFilteredEmail = (email) => {
  if (!email) return false;
  const lower = email.toLowerCase();
  if (FILTERED_EMAILS.includes(lower)) return true;
  return FILTERED_DOMAINS.some((domain) => lower.endsWith(domain));
};

const AdminDropoutAnalysis = ({ firebase, cachedUsers, cachedClassrooms }) => {
  const [loading, setLoading] = useState(true);
  const [computingRisk, setComputingRisk] = useState(false);
  const [allClassrooms, setAllClassrooms] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [userRiskData, setUserRiskData] = useState([]);
  const dropdownRef = useRef(null);

  // Load saved selection
  useEffect(() => {
    const saved = getLocalStorageData(STORAGE_KEY);
    if (saved && Array.isArray(saved)) {
      setSelectedClassIds(saved);
    }
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Process cached classrooms and users
  const fetchData = useCallback(() => {
    if (!cachedUsers || !cachedClassrooms) return;
    setLoading(true);

    const classrooms = Object.keys(cachedClassrooms)
      .map((id) => ({
        id,
        ...cachedClassrooms[id],
      }))
      .filter((c) => c.className)
      .sort((a, b) => (a.className || "").localeCompare(b.className || ""));

    const users = Object.keys(cachedUsers).map((key) => ({
      ...cachedUsers[key],
      uid: key,
    }));

    setAllClassrooms(classrooms);
    setAllUsers(users);
    setLoading(false);
  }, [cachedUsers, cachedClassrooms]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter users by selected classrooms
  const filteredUsers = useMemo(() => {
    if (selectedClassIds.length === 0) return [];

    const selectedClassNames = new Set(
      allClassrooms
        .filter((c) => selectedClassIds.includes(c.id))
        .map((c) => c.className)
    );

    return allUsers.filter(
      (u) =>
        selectedClassNames.has(u.classId) &&
        !u.isMentor &&
        !u.isAdmin &&
        !isFilteredEmail(u.email)
    );
  }, [allUsers, allClassrooms, selectedClassIds]);

  const activeFilteredUsers = useMemo(() => {
    return filteredUsers.filter((u) => !u.inactive);
  }, [filteredUsers]);

  // Compute risk data for active students when selection changes
  useEffect(() => {
    if (activeFilteredUsers.length === 0) {
      setUserRiskData([]);
      return;
    }

    const computeRiskData = async () => {
      setComputingRisk(true);
      try {
        const questions = await riskQuestionsService(firebase).getAllQuestions();

        const riskData = await Promise.all(
          activeFilteredUsers.map(async (user) => {
            let lsiData = [];
            const lsiDoc = await firebase
              .preLsiAssessmentResultsNew(user.uid)
              .get();
            if (lsiDoc.exists) {
              const data = lsiDoc.data();
              if (data.lsiAnswers) {
                lsiData = Object.entries(data.lsiAnswers).map(
                  ([key, value]) => ({ ...value, id: key })
                );
              }
            }

            const dateOfBirth = lsiData.find(
              (d) => d.id === "SrtWJWDRCPAwFIowSFiQ"
            )?.value;
            if (dateOfBirth) {
              const parsedDate = moment(dateOfBirth, "DD/MM/YYYY");
              if (parsedDate.isValid()) {
                const age = moment().diff(parsedDate, "years");
                if (!isNaN(age)) user.age = age;
              }
            }

            const highRiskSpecificQuestions = [
              ...new Set(
                Object.entries(HIGH_RISK_SPECIFIC_QUESTIONS)
                  .filter(([key]) => {
                    const foundQuestion = questions.find((q) => q.id === key);
                    const foundAnswer = lsiData.find((d) => d.id === key);
                    if (foundQuestion && foundAnswer) {
                      return foundQuestion.options?.some(
                        (opt) => opt.isRisky && opt.value === foundAnswer.value
                      );
                    }
                    return false;
                  })
                  .map(([, value]) => value)
              ),
            ];

            const overallRisk = calculateOverallRisk(
              questions,
              lsiData,
              true,
              null
            );
            const categoryRisks = Object.keys(RISK_FACTORS).map(
              (category) => ({
                category,
                label: RISK_FACTORS[category],
                risk: calculateCategoryRisk(
                  category,
                  questions,
                  lsiData,
                  null
                ),
              })
            );

            return {
              ...user,
              overallRisk,
              categoryRisks,
              highRiskSpecificQuestions,
              lsiData,
            };
          })
        );

        setUserRiskData(riskData);
      } catch (err) {
        console.error("Error computing risk data:", err);
      }
      setComputingRisk(false);
    };

    computeRiskData();
  }, [activeFilteredUsers, firebase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dropdown helpers
  const handleToggleClassroom = (classId) => {
    setSelectedClassIds((prev) => {
      const next = prev.includes(classId)
        ? prev.filter((id) => id !== classId)
        : [...prev, classId];
      setLocalStorageData(STORAGE_KEY, next);
      return next;
    });
  };

  const getFilteredDropdownItems = () => {
    if (!searchTerm) return allClassrooms;
    const term = searchTerm.toLowerCase();
    return allClassrooms.filter((c) =>
      c.className.toLowerCase().includes(term)
    );
  };

  const handleSelectAll = () => {
    const filtered = getFilteredDropdownItems();
    const allFilteredIds = filtered.map((c) => c.id);
    const allSelected = allFilteredIds.every((id) =>
      selectedClassIds.includes(id)
    );
    const next = allSelected
      ? selectedClassIds.filter((id) => !allFilteredIds.includes(id))
      : [...new Set([...selectedClassIds, ...allFilteredIds])];
    setSelectedClassIds(next);
    setLocalStorageData(STORAGE_KEY, next);
  };

  const handleClearSelection = () => {
    setSelectedClassIds([]);
    setLocalStorageData(STORAGE_KEY, []);
  };

  const filteredDropdownItems = getFilteredDropdownItems();

  if (loading) {
    return (
      <div className="admin-dropout__loading">
        <div className="admin-dropout__spinner" />
        Loading classroom data...
      </div>
    );
  }

  return (
    <div className="admin-dropout__container">
      {/* Classroom Filter */}
      <div className="admin-dropout__filter">
        <div className="admin-dropout__filter-row">
          <div className="admin-dropout__dropdown" ref={dropdownRef}>
            <button
              className="admin-dropout__dropdown-trigger"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {selectedClassIds.length === 0
                ? "Select classrooms..."
                : `${selectedClassIds.length} classroom${selectedClassIds.length > 1 ? "s" : ""} selected`}
              <span className="admin-dropout__dropdown-arrow">
                {dropdownOpen ? "\u25B2" : "\u25BC"}
              </span>
            </button>
            {dropdownOpen && (
              <div className="admin-dropout__dropdown-menu">
                <input
                  className="admin-dropout__dropdown-search"
                  placeholder="Search classrooms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
                <div className="admin-dropout__dropdown-actions">
                  <button onClick={handleSelectAll}>
                    {filteredDropdownItems.every((c) =>
                      selectedClassIds.includes(c.id)
                    )
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                  {selectedClassIds.length > 0 && (
                    <button onClick={handleClearSelection}>Clear</button>
                  )}
                </div>
                <div className="admin-dropout__dropdown-list">
                  {filteredDropdownItems.map((c) => (
                    <label key={c.id} className="admin-dropout__dropdown-item">
                      <input
                        type="checkbox"
                        checked={selectedClassIds.includes(c.id)}
                        onChange={() => handleToggleClassroom(c.id)}
                      />
                      <span>{c.className}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <span className="admin-dropout__filter-count">
            {filteredUsers.length} participants
            {filteredUsers.filter(isDropout).length > 0 &&
              ` (${filteredUsers.filter(isDropout).length} dropped out)`}
          </span>
        </div>
      </div>

      {/* Content */}
      {selectedClassIds.length === 0 ? (
        <div className="admin-dropout__empty-state">
          <div className="admin-dropout__empty-icon">&#128203;</div>
          <h3>Select Classrooms to Analyze</h3>
          <p>
            Choose one or more classrooms above to view dropout analysis across
            those cohorts.
          </p>
        </div>
      ) : computingRisk ? (
        <div className="admin-dropout__loading">
          <div className="admin-dropout__spinner" />
          Computing risk analysis for {activeFilteredUsers.length} active
          participants...
        </div>
      ) : (
        <DropoutAnalysisTab
          totalUsers={filteredUsers}
          users={activeFilteredUsers}
          firebase={firebase}
          userRiskData={userRiskData}
        />
      )}
    </div>
  );
};

export default withFirebase(AdminDropoutAnalysis);
